import { useState, useEffect, useLayoutEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaArrowRight, FaBullhorn, FaLayerGroup } from "react-icons/fa";
import {
  AnimatePresence,
  motion,
  useAnimation,
  useReducedMotion,
} from "framer-motion";
import { toast } from "react-toastify";

import cardArtwork from "../../../assets/course_content_tab/newDesign.png";
import { DarkModeContext } from "../../../Context/DarkModeContext.jsx";
import axiosInstance from "../../../Context/axiosInstance.js";

// Static Tailwind utilities keep the page styling alongside its components.
const courseStyles = {
  page: "min-h-screen px-6 pb-10 pt-[100px] max-[640px]:px-4 max-[640px]:pb-[30px] bg-[var(--content-bg)] text-[var(--content-text)] [&_button]:[-webkit-tap-highlight-color:transparent] [&_a]:[-webkit-tap-highlight-color:transparent] [&_button:focus-visible]:outline [&_button:focus-visible]:outline-[3px] [&_button:focus-visible]:outline-blue-400 [&_button:focus-visible]:outline-offset-4 [&_a:focus-visible]:outline [&_a:focus-visible]:outline-[3px] [&_a:focus-visible]:outline-blue-400 [&_a:focus-visible]:outline-offset-4",
  light:
    "[--content-bg:#f2f6fa] [--content-text:#102d47] [--content-muted:#526a80] [--content-panel:#fff] [--content-raised:#edf4fa] [--content-border:#d4e3ef] [--content-badge:#06315b]",
  navy: "[--content-bg:#031d32] [--content-text:#f3f6fc] [--content-muted:#bfccdc] [--content-panel:#07263e] [--content-raised:#0d2d47] [--content-border:#173d59] [--content-badge:#95112c] bg-[radial-gradient(ellipse_at_top_right,#09263f,#021b2e_75%)]",
  container: "mx-auto max-w-[1440px]",
  breadcrumb:
    "mb-[30px] flex flex-wrap items-center gap-2 text-sm [&_button]:grid [&_button]:h-[26px] [&_button]:w-[26px] [&_button]:place-items-center [&_button]:rounded-[7px] [&_button]:bg-[var(--content-raised)] [&_a]:ml-2.5 [&>span:last-child]:[overflow-wrap:anywhere]",
  heading:
    "mx-4 mb-[30px] flex items-center justify-between gap-6 max-[640px]:mx-0 [&>div]:min-w-0 [&_h1]:mt-2.5 [&_h1]:text-[clamp(36px,4vw,54px)] [&_h1]:font-extrabold [&_h1]:leading-[1.12] [&_h1]:[overflow-wrap:anywhere]",
  grade:
    "inline-flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl bg-[var(--content-badge)] px-[15px] py-1.5 text-xs uppercase text-white animate-courseBadgeIn motion-reduce:animate-none",
  description: "mt-1 text-[clamp(18px,2vw,26px)] [overflow-wrap:anywhere]",
  meta: "mt-5 flex h-9 items-center gap-[15px] overflow-x-auto text-sm",
  badgePlaceholder:
    "inline-flex h-[30px] w-[110px] shrink-0 rounded-xl bg-[var(--content-border)]",
  motto:
    "relative shrink-0 -rotate-[7deg] border-l border-[var(--content-border)] pl-7 font-[Georgia,serif] text-[26px] italic leading-[1.3] text-[var(--content-muted)] max-[640px]:hidden [&_span]:ml-[25px] [&_span]:mt-2.5 [&_span]:block [&_span]:h-1 [&_span]:w-[100px] [&_span]:-rotate-[8deg] [&_span]:rounded-[100%] [&_span]:bg-[#ec0739]",
  section:
    "mb-5 [&_h2]:text-[27px] [&_h2]:font-bold [&_h2]:leading-[1.3] max-[640px]:[&_h2]:text-2xl",
  sectionDescription:
    "mb-[13px] mt-[5px] text-left text-sm leading-[1.7] text-[var(--content-muted)]",
  learnGrid: "grid grid-cols-2 gap-[18px] max-[1250px]:grid-cols-1",
  practiceGrid:
    "grid grid-cols-3 gap-[18px] max-[1250px]:grid-cols-2 max-[640px]:grid-cols-1",
  card: "relative block w-full min-w-0 overflow-hidden rounded-[13px] bg-no-repeat text-left text-white shadow-[0_4px_16px_#00122212] duration-200 ease-[ease] motion-reduce:transition-none",
  availableCard:
    "transition-[transform,box-shadow] hover:-translate-y-[3px] hover:shadow-[0_10px_26px_#00122235] motion-reduce:hover:transform-none",
  unavailableCard: "cursor-default opacity-40 grayscale-[0.5]",
  learnCard:
    "aspect-[3.5] min-h-[190px] max-[1250px]:min-h-[205px] max-[640px]:min-h-[240px]",
  improveCard: "aspect-[2.1] min-h-[235px]",
  cardCopy:
    "absolute [&_h3]:mb-2 [&_h3]:font-[750] [&_h3]:leading-[1.15] [&_p]:text-left [&_p]:text-sm [&_p]:leading-[1.65] max-[640px]:[&_p]:text-[13px] max-[640px]:[&_p]:leading-[1.7]",
  learnCopy:
    "left-[19%] top-[10%] w-[39%] [&_h3]:text-[25px] max-[640px]:w-[43%] max-[640px]:[&_h3]:text-[22px]",
  improveCopy:
    "left-1/4 top-[11%] w-[44%] [&_h3]:text-[clamp(17px,1.4vw,23px)] max-[640px]:[&_h3]:text-xl",
  cardAction:
    "absolute bottom-[10%] inline-flex min-h-11 items-center justify-center gap-3.5 rounded-xl py-[9px] text-sm font-bold text-white",
  learnAction: "left-[19%] px-5 max-[640px]:left-[6%]",
  improveAction: "left-[5%] max-w-[67%] px-4",
  announcements:
    "rounded-xl border border-[var(--content-border)] bg-[var(--content-panel)] p-4",
  announcementsHeading:
    "mb-3.5 flex items-center gap-4 max-[640px]:flex-wrap max-[640px]:gap-3 [&>div]:min-w-0 [&>div]:flex-1 [&_h2]:text-lg [&_h2]:font-bold [&_p]:mt-[5px] [&_p]:text-xs [&_p]:text-[var(--content-muted)] [&>button]:inline-flex [&>button]:shrink-0 [&>button]:items-center [&>button]:gap-3 [&>button]:rounded-xl [&>button]:border [&>button]:border-[var(--content-border)] [&>button]:bg-[var(--content-raised)] [&>button]:px-[18px] [&>button]:py-3 [&>button]:text-xs [&>button]:font-bold max-[640px]:[&>button]:ml-[38px]",
  announcement:
    "mt-2 flex items-start gap-[18px] rounded-xl bg-[var(--content-raised)] p-4 text-sm max-[640px]:flex-wrap max-[640px]:gap-2.5 [&>div]:min-w-0 [&>div]:flex-1 [&>div]:[overflow-wrap:anywhere] [&_h3]:font-bold [&_p]:mt-1 [&_p]:whitespace-pre-wrap [&_p]:text-xs [&_p]:text-[var(--content-muted)]",
  announcementDot: "mt-[5px] h-3 w-3 shrink-0 rounded-full bg-[#f30b46]",
  announcementDate:
    "text-xs text-[var(--content-muted)] max-[640px]:w-full max-[640px]:pl-[22px]",
};

const artworkRegions = {
  videos: [28, 49, 1007, 286],
  materials: [1061, 49, 988, 286],
  practice: [28, 372, 665, 305],
  mistakes: [724, 372, 628, 305],
  performance: [1385, 372, 663, 305],
};

function ContentCard({
  kind,
  title,
  description,
  action,
  href,
  onClick,
  disabled = false,
}) {
  const shake = useAnimation();
  const reduceMotion = useReducedMotion();
  const [x, y, width, height] = artworkRegions[kind];
  const practice = !["videos", "materials"].includes(kind);
  const Tag = href && !disabled ? motion.a : motion.button;
  const handleUnavailableClick = () => {
    if (!reduceMotion) {
      shake.stop();
      shake.start({
        x: [0, -8, 8, -6, 6, -3, 3, 0],
        transition: { duration: 0.4 },
      });
    }
    toast.info("Not available yet.", {
      toastId: `course-card-unavailable-${kind}`,
    });
  };
  return (
    <Tag
      className={`${courseStyles.card} ${disabled ? courseStyles.unavailableCard : courseStyles.availableCard} ${practice ? courseStyles.improveCard : courseStyles.learnCard}`}
      animate={shake}
      style={{
        backgroundImage: `url(${cardArtwork})`,
        backgroundSize: `${(2075 / width) * 100}% ${(758 / height) * 100}%`,
        backgroundPosition: `${(x / (2075 - width)) * 100}% ${
          (y / (758 - height)) * 100
        }%`,
      }}
      {...(href && !disabled
        ? { href, rel: "noopener noreferrer" }
        : {
            type: "button",
            onClick: disabled ? handleUnavailableClick : onClick,
          })}
      aria-disabled={disabled || undefined}
      aria-label={href && !disabled ? `${action} (opens in a new tab)` : action}
    >
      <div
        className={`${courseStyles.cardCopy} ${practice ? courseStyles.improveCopy : courseStyles.learnCopy}`}
      >
        <h3>{title}</h3>
        <p lang="ar" dir="rtl">
          {description}
        </p>
      </div>
      <span
        className={`${courseStyles.cardAction} ${practice ? courseStyles.improveAction : courseStyles.learnAction} ${["videos", "practice"].includes(kind) ? "bg-[#e70736]" : "bg-[#0752a5]"}`}
      >
        {action}
        <FaArrowRight aria-hidden="true" />
      </span>
    </Tag>
  );
}

export default function CourseDetailPage() {
  const { isDarkMode } = useContext(DarkModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const course = location.state?.course;
  const folder = location.state?.folder;
  const courseId = course?.id;
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.key, courseId]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [retry, setRetry] = useState(0);
  const [courseLinks, setCourseLinks] = useState({
    practiceUrl: course?.practiceUrl || "",
    reviewMyMistakesUrl: course?.reviewMyMistakesUrl || "",
    myPerformanceUrl: course?.myPerformanceUrl || "",
  });
  const [linksLoaded, setLinksLoaded] = useState(false);

  useEffect(() => {
    if (!courseId) return;

    const controller = new AbortController();

    setLinksLoaded(false);

    axiosInstance
      .get(`/courses/${courseId}`, { signal: controller.signal })
      .then(({ data }) => {
        setCourseLinks(data);
        setLinksLoaded(true);
      })
      .catch((error) => {
        if (error.name !== "CanceledError") {
          console.error("Failed to load course links:", error);
          setLinksLoaded(true);
        }
      });

    return () => controller.abort();
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    setShowAll(false);
    axiosInstance
      .get(`/courses/${courseId}/announcements?page=1&pageSize=99999`, {
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (active) setAnnouncements(data.response || []);
      })
      .catch(() => {
        if (active)
          setError("We couldn't load course announcements. Please try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [courseId, retry]);

  if (!course)
    return (
      <div
        className={`${courseStyles.page} ${isDarkMode ? courseStyles.navy : courseStyles.light}`}
        data-theme={isDarkMode ? "navy" : "light"}
      >
        <h1 className="text-3xl font-bold">Choose a course to continue</h1>
        <Link to="/my-courses" className="mt-5 inline-flex underline">
          Back to My Courses
        </Link>
      </div>
    );

  const openContent = (route) => {
    const slug = encodeURIComponent(
      course.name.replace(/\s+/g, "_").replace(/\//g, "-").toLowerCase(),
    );
    const basePath = location.pathname.startsWith("/my-courses")
      ? `/my-courses/${slug}`
      : `/student-folders/${folder?.name}/courses/${slug}`;
    navigate(`${basePath}/${route}`, { state: { course, folder } });
  };

  const gradeLevel = courseLinks.gradeLevel ?? course.gradeLevel;
  const isLegacyCustomLevel = gradeLevel?.trim().toLowerCase() === "custom";
  const isCustomCourse =
    courseLinks.isCustom ?? course.isCustom ?? isLegacyCustomLevel;

  return (
    <div
      className={`${courseStyles.page} ${isDarkMode ? courseStyles.navy : courseStyles.light}`}
      data-theme={isDarkMode ? "navy" : "light"}
    >
      <div className={courseStyles.container}>
        <nav aria-label="Course navigation" className={courseStyles.breadcrumb}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <IoIosArrowBack />
          </button>
          <button
            type="button"
            onClick={() => navigate(1)}
            aria-label="Go forward"
          >
            <IoIosArrowForward />
          </button>
          <Link to="/my-courses">My Courses</Link>
          <span aria-hidden="true">›</span>
          <span>{course.name}</span>
        </nav>
        <header className={courseStyles.heading}>
          <div>
            <h1>{course.name}</h1>
            {course.description && (
              <p className={courseStyles.description}>{course.description}</p>
            )}
            <div className={courseStyles.meta} aria-label="Course badges">
              <>
                {isCustomCourse && (
                  <span className={courseStyles.grade}>Custom Course</span>
                )}
                {gradeLevel && !isLegacyCustomLevel && (
                  <span className={courseStyles.grade}>{gradeLevel}</span>
                )}
                {folder?.name && (
                  <span className={courseStyles.grade}>
                    <FaLayerGroup aria-hidden="true" />
                    {folder.name}
                  </span>
                )}
              </>
            </div>
          </div>
          <p className={courseStyles.motto} aria-hidden="true">
            Better Students
            <br />
            Brighter Futures
            <span />
          </p>
        </header>

        <section
          aria-labelledby="course-learn-heading"
          className={courseStyles.section}
        >
          <h2 id="course-learn-heading">Learn</h2>
          <p className={courseStyles.sectionDescription} lang="ar" dir="rtl">
            المحاضرات والملازم الخاصة بالكورس متاحة لك هنا.
          </p>
          <motion.div
            className={courseStyles.learnGrid}
            initial={{ opacity: 0, x: -35 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.45,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <ContentCard
              kind="videos"
              title="Videos"
              description="اتفرج على محاضرات الكورس من هنا، في أي وقت يناسبك."
              action="Open Lectures"
              onClick={() => openContent("videos")}
            />
            <ContentCard
              kind="materials"
              title="Materials"
              description="نزل كل الملازم والـ PDFs الخاصة بالكورس من هنا."
              action="View Materials"
              onClick={() => openContent("materials")}
            />
          </motion.div>
        </section>

        <section
          aria-labelledby="course-practice-heading"
          className={courseStyles.section}
        >
          <h2 id="course-practice-heading">Practice & Improve</h2>

          <p className={courseStyles.sectionDescription} lang="ar" dir="rtl">
            استخدم EduHigh عشان تحل مسائل، تراجع أخطاءك، وتتابع تطورك في الكورس.
          </p>
          <motion.div
            className={courseStyles.practiceGrid}
            initial="hidden"
            animate={"show"}
            variants={{
              hidden: {},
              show: {
                transition: {
                  staggerChildren: 0.08,
                },
              },
            }}
          >
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.35 }}
            >
              <ContentCard
                kind="practice"
                title="Practice"
                description="حل مسائل، امتحن نفسك، واتدرب على كل أفكار الكورس من هنا."
                action="Start Practice"
                href={courseLinks.practiceUrl?.trim()}
                disabled={linksLoaded && !courseLinks.practiceUrl?.trim()}
              />
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.35 }}
            >
              <ContentCard
                kind="mistakes"
                title="Review My Mistakes"
                description="راجع أخطاءك، افهمها، واتدرب عليها من جديد من هنا."
                action="Review Mistakes"
                href={courseLinks.reviewMyMistakesUrl?.trim()}
                disabled={
                  linksLoaded && !courseLinks.reviewMyMistakesUrl?.trim()
                }
              />
            </motion.div>
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.35 }}
            >
              <ContentCard
                kind="performance"
                title="My Performance"
                description="تابع مستواك، دقة حلك، ونقاط قوتك وضعفك وتقدمك في الكورس."
                action="View Performance"
                href={courseLinks.myPerformanceUrl?.trim()}
                disabled={linksLoaded && !courseLinks.myPerformanceUrl?.trim()}
              />
            </motion.div>
          </motion.div>
        </section>

        <section
          aria-labelledby="course-announcements-heading"
          className={courseStyles.announcements}
        >
          <div className={courseStyles.announcementsHeading}>
            <FaBullhorn aria-hidden="true" className="text-2xl" />
            <div>
              <h2 id="course-announcements-heading">Course Announcements</h2>
              <p>
                Stay updated with the latest news, updates, and important
                information about this course.
              </p>
            </div>
            {announcements.length > 1 && (
              <button
                type="button"
                onClick={() => setShowAll((value) => !value)}
                aria-expanded={showAll}
              >
                {showAll ? "Show Less" : "View All"}
                <FaArrowRight aria-hidden="true" />
              </button>
            )}
          </div>
          {loading ? (
            <p role="status" className={courseStyles.announcement}>
              Loading announcements...
            </p>
          ) : error ? (
            <div role="alert" className={courseStyles.announcement}>
              {error}
              <button
                type="button"
                className="ml-3 underline"
                onClick={() => setRetry((value) => value + 1)}
              >
                Try again
              </button>
            </div>
          ) : announcements.length ? (
            <motion.div
              layout
              initial={false}
              transition={{
                layout: {
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1],
                },
              }}
            >
              <AnimatePresence initial={false}>
                {(showAll ? announcements : announcements.slice(0, 1)).map(
                  ({ id, title, description, date }, index) => (
                    <motion.article
                      layout
                      key={id}
                      initial={{
                        opacity: 0,
                        height: 0,
                        y: -12,
                        marginTop: 0,
                      }}
                      animate={{
                        opacity: 1,
                        height: "auto",
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        height: 0,
                        y: -12,
                        marginTop: 0,
                      }}
                      transition={{
                        duration: 0.32,
                        delay: showAll ? index * 0.04 : 0,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className={`${courseStyles.announcement} overflow-hidden`}
                    >
                      <span
                        className={courseStyles.announcementDot}
                        aria-hidden="true"
                      />

                      <div>
                        <h3>{title}</h3>
                        <p>{description}</p>
                      </div>

                      {date && (
                        <span className={courseStyles.announcementDate}>
                          {date}
                        </span>
                      )}
                    </motion.article>
                  ),
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <p className={courseStyles.announcement}>
              No announcements available.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
